/**
 * Окна сущностей для навигатора.
 *
 * Каждое окно собирается по одному ключу, без строки списка страницы: то, чего
 * в окне нет, догружается загрузчиками из `loaders.ts`, а то, что окно умеет
 * только в паре со страницей (удаление и копирование макета, ИИ-предложения,
 * теги, открытие тематики), здесь осознанно ограничено и говорит об этом
 * подсказкой. Закрытие окна (крестик, Esc) — это «Назад» по стеку.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { DesignCardModal } from '../../features/designs-v2/components/DesignCardModal'
import { ProductCardModal } from '../../features/products-v2/components/ProductCardModal'
import { gingerbreadProductWindow } from '../../features/gingerbread-catalog/gingerbreadProductWindow'
import { PimDraftCardModal } from '../../features/marketplace-catalog/components/PimDraftCardModal'
import { WbProductModal } from '../../features/wildberries/catalog/WbProductModal'
import { OzProductModal } from '../../features/ozon/catalog/OzProductModal'
import { YmProductModal } from '../../features/yandex-market/catalog/YmProductModal'
import { SiteCardModal } from '../../features/site/SiteCardModal'
import type { SiteProductCard } from '../../features/site/types'
import {
  pimMarketplaceCardsAPI,
  type PimMarketplaceCard,
} from '../../features/marketplace-catalog/pim-drafts-api'
import {
  PublishCardsOverlays,
  targetsFromCards,
  usePublishCards,
} from '../marketplace-publish'
import { useToastStore } from '../../stores/toastStore'
import { EntityModalShell } from './EntityModalShell'
import { Button } from '../ui/Button'
import type { EntityTarget } from './navigatorContext'
import {
  loadDraftCard,
  loadProductCategories,
  loadSellerName,
  loadSiteCard,
} from './loaders'

export interface EntityWindowProps {
  /** Есть куда возвращаться: показывает «Назад» и включает Alt+←. */
  canGoBack: boolean
  /** Снять это окно со стека. */
  onBack: () => void
  /** Положить следующее окно поверх этого. */
  onOpen: (target: EntityTarget) => void
  /** Закрыть весь стек (уход на другую страницу). */
  onCloseAll: () => void
}

const noop = () => undefined

const responseMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback

/** Окно макета. Правки макета сохраняются самим окном; удаление, копирование и ИИ — на странице «Макеты». */
export const DesignWindow: React.FC<EntityWindowProps & { code: number | string }> = ({
  code,
  canGoBack,
  onBack,
  onOpen,
}) => {
  const showToast = useToastStore((state) => state.showToast)
  const numeric = Number(code)
  const pageOnly = (what: string) => () =>
    showToast(`${what} — на странице «Макеты»`, 'info')
  return (
    <DesignCardModal
      isOpen
      designCode={Number.isFinite(numeric) ? numeric : null}
      codes={Number.isFinite(numeric) ? [numeric] : []}
      onNavigate={noop}
      onClose={onBack}
      onSaved={noop}
      onDelete={pageOnly('Удаление макета')}
      onDuplicate={pageOnly('Копирование макета')}
      onOpenTheme={pageOnly('Просмотр тематики')}
      onOpenProduct={(article) => onOpen({ kind: 'product', article })}
      tags={[]}
      tagsAvailable={false}
      onCreateTag={async () => null}
      onOpenAi={pageOnly('ИИ-предложения')}
      canGoBack={canGoBack}
      onBack={onBack}
    />
  )
}

/** Окно товара. */
export const ProductWindow: React.FC<EntityWindowProps & { article: string }> = ({
  article,
  canGoBack,
  onBack,
}) => {
  const showToast = useToastStore((state) => state.showToast)
  const [categories, setCategories] = useState<Array<{ id: number; description: string }>>([])
  useEffect(() => {
    let cancelled = false
    void loadProductCategories().then((rows) => {
      if (!cancelled) setCategories(rows)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return (
    <ProductCardModal
      isOpen
      article={article}
      articles={[article]}
      onNavigate={noop}
      onClose={onBack}
      onSaved={noop}
      onDelete={() => showToast('Удаление товара — на странице «Товары»', 'info')}
      categories={categories}
      extension={gingerbreadProductWindow}
      layer="nested"
      canGoBack={canGoBack}
      onBack={onBack}
    />
  )
}

/** Черновик загружен: публикация, исключение и удаление работают теми же запросами, что на странице. */
const DraftWindowLoaded: React.FC<
  EntityWindowProps & { card: PimMarketplaceCard; onChanged: () => void }
> = ({ card, canGoBack, onBack, onCloseAll, onOpen, onChanged }) => {
  const navigate = useNavigate()
  const showToast = useToastStore((state) => state.showToast)
  const publish = usePublishCards(card.marketplace, {
    mode: 'preview',
    onNavigate: (to: any) => {
      onCloseAll()
      navigate(to)
    },
  })
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const setExclusion = useCallback(
    async (target: PimMarketplaceCard, excluded: boolean, reason?: string | null) => {
      try {
        await pimMarketplaceCardsAPI.setPublishExclusion([target.id], excluded, reason)
        showToast(excluded ? 'Исключено из выгрузки: 1' : 'Возвращено в выгрузку: 1', 'success')
        onChanged()
      } catch (error: any) {
        showToast(responseMessage(error, 'Не удалось изменить признак выгрузки'), 'error')
      }
    },
    [onChanged, showToast],
  )

  const confirmDelete = async () => {
    setDeleting(true)
    try {
      const result = await pimMarketplaceCardsAPI.deleteDrafts([card.id])
      if (result.deleted.length > 0) {
        showToast('Удалено черновиков: 1', 'success')
        setDeleteOpen(false)
        onBack()
      } else {
        showToast('Не удалось удалить: черновик уже выгружен или отправляется', 'error')
        setDeleteOpen(false)
      }
    } catch (error: any) {
      showToast(responseMessage(error, 'Не удалось удалить черновик'), 'error')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <PimDraftCardModal
        card={card}
        cards={[card]}
        onNavigate={noop}
        onClose={onBack}
        onPublish={(target) => {
          if (!publish.pending) {
            publish.requestPublish(targetsFromCards(target.article, [target], target.marketplace))
          }
        }}
        onSetExclusion={setExclusion}
        onDelete={() => setDeleteOpen(true)}
        canGoBack={canGoBack}
        onBack={onBack}
      />
      <ConfirmDialog
        isOpen={deleteOpen}
        layer="nested"
        danger
        loading={deleting}
        title="Удалить черновик?"
        confirmLabel="Удалить"
        onClose={() => {
          if (!deleting) setDeleteOpen(false)
        }}
        onConfirm={() => void confirmDelete()}
        message={
          <p>
            Черновик карточки «{card.article}» будет удалён без возможности восстановления.
            {' '}Визуалы этой карточки будут удалены; файлы печати и общие слайды воронки останутся — они принадлежат товару.
          </p>
        }
      />
      <PublishCardsOverlays controller={publish} />
    </>
  )
}

/** Окно черновика ПИМ по id: строка догружается, пока идёт загрузка — каркас со скелетонами. */
export const DraftWindow: React.FC<
  EntityWindowProps & { cardId: number; sellerId: number; marketplace: 'wb' | 'ozon' | 'ym' }
> = (props) => {
  const { cardId, sellerId, marketplace, onBack } = props
  const [card, setCard] = useState<PimMarketplaceCard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((value) => value + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    void loadDraftCard(sellerId, marketplace, cardId, controller.signal)
      .then((row) => {
        if (controller.signal.aborted) return
        if (row) setCard(row)
        else setError('Черновик не найден: возможно, он уже выгружен или удалён')
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return
        if (requestError?.name === 'CanceledError' || requestError?.code === 'ERR_CANCELED') return
        setError(responseMessage(requestError, 'Не удалось загрузить черновик'))
      })
    return () => controller.abort()
  }, [cardId, sellerId, marketplace, tick])

  if (card) return <DraftWindowLoaded {...props} card={card} onChanged={reload} />

  return (
    <EntityModalShell
      isOpen
      ariaLabel={`Черновик ПИМ ${cardId}`}
      kind={{ label: 'Черновик' }}
      primaryId={{ value: `#${cardId}`, label: 'номер черновика' }}
      keys={[]}
      activeKey={null}
      onNavigate={noop}
      onClose={onBack}
      canGoBack={props.canGoBack}
      onBack={onBack}
      loading={!error}
      allowFullscreen={false}
      banner={
        error ? (
          <div className="shrink-0 border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/40 dark:text-red-300">
            {error}
            <Button className="ml-3" size="sm" variant="secondary" onClick={reload}>
              Повторить
            </Button>
          </div>
        ) : undefined
      }
    >
      {null}
    </EntityModalShell>
  )
}

/** Название кабинета для окон ВБ/ОЗОН/Я.Маркета: подгружается без блокировки окна. */
const useSellerName = (sellerId: number | string | null | undefined): string | null => {
  const [name, setName] = useState<string | null>(null)
  useEffect(() => {
    if (sellerId === null || sellerId === undefined || sellerId === '') return undefined
    let cancelled = false
    void loadSellerName(sellerId).then((value) => {
      if (!cancelled) setName(value)
    })
    return () => {
      cancelled = true
    }
  }, [sellerId])
  return name
}

/** Окно карточки площадки (ВБ, ОЗОН, Я.Маркет): полная карточка догружается самим окном по ключу. */
export const MarketplaceCardWindow: React.FC<
  EntityWindowProps & {
    marketplace: 'wb' | 'ozon' | 'ym'
    sellerId: number | string | null | undefined
    cardKey: string
  }
> = ({ marketplace, sellerId, cardKey, canGoBack, onBack }) => {
  const showToast = useToastStore((state) => state.showToast)
  const sellerName = useSellerName(sellerId)
  const missingSeller = sellerId === null || sellerId === undefined || sellerId === ''
  const warned = useRef(false)
  useEffect(() => {
    if (missingSeller && !warned.current) {
      warned.current = true
      showToast('Не указан кабинет карточки площадки', 'error')
      onBack()
    }
  }, [missingSeller, onBack, showToast])
  if (missingSeller) return null
  const common = {
    sellerId: String(sellerId),
    sellerName,
    keys: [cardKey],
    rows: [] as never[],
    onNavigate: noop,
    onClose: onBack,
    canGoBack,
    onBack,
  }
  switch (marketplace) {
    case 'wb':
      return <WbProductModal {...common} nmId={cardKey} onSync={noop} />
    case 'ozon':
      return <OzProductModal {...common} offerId={cardKey} onSync={noop} />
    case 'ym':
      return <YmProductModal {...common} offerId={cardKey} />
    default:
      return null
  }
}

/** Окно карточки сайта по артикулу. */
export const SiteWindow: React.FC<EntityWindowProps & { article: string }> = ({
  article,
  canGoBack,
  onBack,
}) => {
  const [card, setCard] = useState<SiteProductCard | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setCard(null)
    void loadSiteCard(article)
      .then((value) => {
        if (!cancelled) setCard(value)
      })
      .catch(() => {
        if (!cancelled) setCard(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [article])
  return (
    <SiteCardModal
      article={article}
      card={card}
      loading={loading}
      keys={[article]}
      onNavigate={noop}
      onClose={onBack}
      canGoBack={canGoBack}
      onBack={onBack}
    />
  )
}

/** Выбор окна по цели навигатора. */
export const EntityWindow: React.FC<EntityWindowProps & { target: EntityTarget }> = ({
  target,
  ...rest
}) => {
  switch (target.kind) {
    case 'design':
      return <DesignWindow {...rest} code={target.code} />
    case 'product':
      return <ProductWindow {...rest} article={target.article} />
    case 'draft':
      return (
        <DraftWindow
          {...rest}
          cardId={target.cardId}
          sellerId={target.sellerId}
          marketplace={target.marketplace}
        />
      )
    case 'mp_card':
      return target.marketplace === 'site' ? (
        <SiteWindow {...rest} article={target.key} />
      ) : (
        <MarketplaceCardWindow
          {...rest}
          marketplace={target.marketplace}
          sellerId={target.sellerId}
          cardKey={target.key}
        />
      )
    default:
      return null
  }
}
